/**
 * Keep presigned bearer URLs out of analytics (#2326 round 13).
 *
 * A presigned Spaces URL is a **bearer capability for one private object**.
 * Rendering it as a DOM attribute (`href` / `src`) is fine for the USER — the
 * surface's tenancy check already authorised them — but the browser user's
 * authorisation does NOT extend to the analytics vendor: PostHog autocapture
 * serialises element attributes (`$elements[].attr__href`) and rrweb replay
 * snapshots the whole DOM, so without this mask every consented session ships
 * live bearer URLs for private compliance evidence (diary photos, certifications,
 * company documents), plus the user/project identifiers inside native key
 * paths. Signature expiry bounds the exposure in time; it does not un-send it.
 *
 * The marker is the SIGNATURE parameter, never the host: a stored origin URL
 * and a presigned one differ precisely by `X-Amz-Signature=`, so this mask
 * cannot touch the legitimate stored/exported URLs that analytics may carry —
 * the reason `lib/analytics/suppressed-paths.ts` was rejected for this in the
 * token-pii-sinks inventory (that module scrubs stored URL VALUES by host).
 *
 * Two consumers, both chokepoints rather than call sites:
 *  - `before_send` in `components/analytics/posthog-provider.tsx` redacts the
 *    marker from EVERY dispatched capture event (autocapture `$elements`,
 *    custom events, dead clicks) via {@link redactPresignedUrlsFromEvent};
 *  - `session_recording.maskAttributeFn` masks the attribute in rrweb's
 *    serialized representation BEFORE compression, via {@link maskPresignedUrlValue}.
 */
export const PRESIGNED_URL_MARKER = "X-Amz-Signature=";

/** What a presigned URL is replaced with. Carries no key path — the path
 * itself names users, projects and documents. */
export const REDACTED_PRESIGNED_URL = "[redacted presigned url]";

/** True when a string value is a presigned object URL (any host). */
export function isPresignedUrlValue(value: string): boolean {
  return value.includes(PRESIGNED_URL_MARKER);
}

/** Mask a string if it is presigned; pass everything else through. */
export function maskPresignedUrlValue(value: string): string {
  return isPresignedUrlValue(value) ? REDACTED_PRESIGNED_URL : value;
}

/**
 * Deep-copy `event` with every presigned-URL string replaced by the redaction
 * marker — **copy-on-write**: an event carrying no signature is returned as
 * the SAME object, so the clean pass-through path keeps referential identity
 * (pinned by the before-send consent tests). Walks plain objects and arrays
 * only (mirroring the advertising sanitizer's prototype guard) and is
 * cycle-safe via the seen map; the input is never mutated — the SDK documents
 * `before_send` as "edit data before it is sent".
 */
export function redactPresignedUrlsFromEvent<
  T extends { properties?: unknown; $set?: unknown; $set_once?: unknown },
>(event: T): T {
  return redactValue(event, new WeakMap<object, unknown>()).value as T;
}

type Redaction = { value: unknown; changed: boolean };

const UNCHANGED = (value: unknown): Redaction => ({ value, changed: false });

function redactValue(value: unknown, seen: WeakMap<object, unknown>): Redaction {
  if (typeof value === "string") {
    return isPresignedUrlValue(value)
      ? { value: REDACTED_PRESIGNED_URL, changed: true }
      : UNCHANGED(value);
  }
  if (value === null || typeof value !== "object") return UNCHANGED(value);
  const existing = seen.get(value);
  if (existing !== undefined) return existing as Redaction;

  // Seed `seen` with a MUTABLE holder BEFORE recursing so a cycle resolves
  // to this same object; the fields are filled in once the children settle.
  const holder: Redaction = { value, changed: false };
  seen.set(value, holder);

  // Children are kept as Redaction objects and dereferenced only AFTER the
  // parent's copy exists, so a cyclic child resolves to the copy, never to
  // the (still-signed) original.
  if (Array.isArray(value)) {
    const items: Redaction[] = value.map((item) => redactValue(item, seen));
    if (items.every((r) => !r.changed)) return holder;
    const copy: unknown[] = [];
    holder.value = copy;
    holder.changed = true;
    for (const item of items) copy.push(item.value);
    return holder;
  }
  if (
    Object.getPrototypeOf(value) !== Object.prototype &&
    Object.getPrototypeOf(value) !== null
  ) {
    return holder;
  }
  const entries = Object.entries(value).map(([key, nested]) => ({
    key,
    redaction: redactValue(nested, seen),
  }));
  if (entries.every((e) => !e.redaction.changed)) return holder;
  const copy: Record<string, unknown> = {};
  holder.value = copy;
  holder.changed = true;
  for (const entry of entries) copy[entry.key] = entry.redaction.value;
  return holder;
}
